import * as React from "react";
import clsx from "clsx";
import { Ban, Edit, MoreVertical, Pin, Share, Trash } from "lucide-react";
import { toast } from "react-toastify";
import { useToggle } from "react-use";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCancelSnapshotCollectionMutation } from "@/modules/snapshots/businessReports.api";
import { RemoveCollectionModal } from "@/modules/snapshots/modals/removeCollectionModal";
import { SnapshotCollectionShareModal } from "@/modules/snapshots/modals/snapshotCollectionShareModal";
import { UpdateCollectionModal } from "@/modules/snapshots/modals/updateCollectionModal";
import type { SnapshotCollection } from "@/modules/snapshots/snapshotCollection.types";
import { getCollectionLockMessage } from "@/views/contract-families/pipeline/pipelineErrors";

import { useFavoriteCollection } from "../hooks/useFavoriteCollection";
import { stopCollectionMenuClickPropagation, stopCollectionMenuTriggerEvent } from "./collectionActionsMenu.events";

interface Props {
  collection: SnapshotCollection;
  triggerClassName?: string;
  triggerVariant?: React.ComponentProps<typeof Button>["variant"];
}

export const CollectionActionsMenu: React.FC<Props> = ({ collection, triggerClassName, triggerVariant = "empty" }) => {
  const [isShareOpen, toggleIsShareOpen] = useToggle(false);
  const [isRenameOpen, toggleIsRenameOpen] = useToggle(false);
  const [isRemoveOpen, toggleIsRemoveOpen] = useToggle(false);

  const { isFavorited, handleToggleFavorite } = useFavoriteCollection(collection.id);
  const [cancelCollection] = useCancelSnapshotCollectionMutation();

  const handleCancel = async () => {
    try {
      await cancelCollection({ id: collection.id }).unwrap();
    } catch (error) {
      toast.error(getCollectionLockMessage(error, "The SnapShot collection could not be cancelled."));
    }
  };

  return (
    <>
      {isShareOpen && (
        <SnapshotCollectionShareModal
          setIsOpen={toggleIsShareOpen}
          isOpen={isShareOpen}
          snapshotCollection={collection}
        />
      )}
      {isRenameOpen && (
        <UpdateCollectionModal snapshotCollection={collection} isOpen={isRenameOpen} setIsOpen={toggleIsRenameOpen} />
      )}
      {isRemoveOpen && (
        <RemoveCollectionModal snapshotCollection={collection} isOpen={isRemoveOpen} setIsOpen={toggleIsRemoveOpen} />
      )}

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant={triggerVariant}
            className={clsx("h-6 w-6 p-0", triggerClassName)}
            onClick={stopCollectionMenuTriggerEvent}
            aria-label={`Open actions for ${collection.name}`}
          >
            <MoreVertical size={14} className="text-gray-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={stopCollectionMenuClickPropagation}>
          <DropdownMenuItem onSelect={handleToggleFavorite} className="cursor-pointer">
            <Pin
              className={clsx("mr-2 h-4 w-4", isFavorited ? "text-yellow-500" : "text-gray-500 hover:text-yellow-500")}
            />
            {isFavorited ? "Unpin" : "Pin"}
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={toggleIsShareOpen} className="cursor-pointer">
            <Share className="mr-2 h-4 w-4" />
            Share
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={toggleIsRenameOpen} className="cursor-pointer">
            <Edit className="mr-2 h-4 w-4" />
            Rename
          </DropdownMenuItem>
          {(collection.status === "IN_PROGRESS" || collection.status === "NEW") && !collection.is_family_collection && (
            <DropdownMenuItem onSelect={() => void handleCancel()} className="cursor-pointer">
              <Ban className="mr-2 h-4 w-4" />
              Cancel
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={toggleIsRemoveOpen} className="cursor-pointer">
            <Trash className="mr-2 h-4 w-4" />
            Remove
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
